import React, { createContext, useContext, useState, useCallback } from "react";

const CustomizedDataContext = createContext();

/**
 * Hook to access the customized data context
 * @returns {Object} The customized data context value containing all data states and functions
 * @throws {Error} If used outside of CustomizedDataProvider
 */
export const useCustomizedData = () => {
	const context = useContext(CustomizedDataContext);
	if (!context) {
		throw new Error("useCustomizedData must be used within a CustomizedDataProvider");
	}
	return context;
};

/**
 * Customized data provider component that manages all customized seeding data states
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Child components to wrap with customized data context
 * @returns {JSX.Element} The customized data provider component
 */
export const CustomizedDataProvider = ({ children }) => {
	// Employee management state
	const [employeeData, setEmployeeData] = useState({
		employees: [],
		employeeName: "",
		employeeManager: "",
		employeeRole: "",
		editingEmployeeId: null,
	});

	// Custom fields state
	const [customFieldData, setCustomFieldData] = useState({
		customFields: [],
		customFieldName: "",
		customFieldType: "",
		customFieldOptions: [],
		editingCustomFieldId: null,
	});

	// Employee Group state
	const [employeeGroupData, setEmployeeGroupData] = useState({
		employeeGroups: [],
		employeeGroupName: "",
		employeeGroupEmployees: [],
		employeeGroupAdmins: [],
		employeeGroupAdminPermissions: null,
		editingEmployeeGroupId: null,
	});
	// Review form state
	const [customReviewFormData, setCustomReviewFormData] = useState({
		customReviewForms: [],
		customReviewFormName: "",
		customReviewQuestions: [
			{
				id: 1,
				type: "",
				question: "",
				options: [],
				required: false,
			},
		],
		editingCustomReviewFormId: null,
	});

	const [reviewTemplateData, setReviewTemplateData] = useState({
		reviewTemplateName: "",
		reviewTemplates: [],
		selectedFormType: null,
		selectedForm: null,
		tempFormData: {},
		editingReviewTemplateId: null,
		forms: [],
		forms_360: [],
		external_forms: [],
	});
	const [reviewCycleData, setReviewCycleData] = useState({
		reviewCycles: [],
		reviewCycleName: "",
		reviewTemplate: "",
		subjects: [],
		startDate: new Date(),
		admin_only: false,
		reviews: [],
		easy_add_configs: [],
		department: null,
		editingReviewCycleId: null,
	});

	const [goalsData, setGoalsData] = useState({
		goals: [],
		goalId: "",
		goalName: "",
		goalDescription: "",
		goalAssignee: [],
		goalOwner: "",
		goalTarget: "",
		goalUnits: "",
		goalValue: "",
		startDate: new Date(),
		editingGoalId: null,
	});

	//************************************************************************************************
	// Employee management functions
	//************************************************************************************************
	const updateEmployeeData = useCallback((updates) => {
		setEmployeeData((prev) => ({ ...prev, ...updates }));
	}, []);

	/**
	 * Add a new employee or update an existing one if in edit mode
	 * @param {Object} employee - The employee object to add or update
	 * @param {string} employee.name - Employee name
	 * @param {string} employee.manager - Employee manager
	 * @param {string} employee.role - Employee role
	 */
	const addEmployee = (employee) => {
		setEmployeeData((prev) => {
			if (prev.editingEmployeeId) {
				return {
					...prev,
					employees: prev.employees.map((emp) =>
						emp.id === prev.editingEmployeeId
							? { ...emp, name: employee.name, manager: employee.manager, role: employee.role }
							: emp
					),
					employeeName: "",
					employeeManager: "",
					employeeRole: "",
					editingEmployeeId: null,
				};
			} else {
				return {
					...prev,
					employees: [employee, ...prev.employees],
					employeeName: "",
					employeeManager: "",
					employeeRole: "",
					editingEmployeeId: null,
				};
			}
		});
	};

	/**
	 * Edit an existing employee by populating form fields with employee data
	 * @param {number} employeeId - The ID of the employee to edit
	 */
	const editEmployee = (employeeId) => {
		const employee = employeeData.employees.find((emp) => emp.id === employeeId);
		if (employee) {
			setEmployeeData((prev) => ({
				...prev,
				employeeName: employee.name,
				employeeManager: employee.manager ? { label: employee.manager, value: employee.manager } : null,
				employeeRole: { value: employee.role, label: employee.role },
				editingEmployeeId: employeeId,
			}));
		}
	};

	/**
	 * Delete an employee from the employees list
	 * @param {number} employeeId - The ID of the employee to delete
	 */
	const deleteEmployee = (employeeId) => {
		setEmployeeData((prev) => ({
			...prev,
			employees: prev.employees.filter((emp) => emp.id !== employeeId),
		}));
	};

	/**
	 * Cancel the current employee edit operation and clear form fields
	 */
	const cancelEditEmployee = () => {
		setEmployeeData((prev) => ({
			...prev,
			employeeName: "",
			employeeManager: "",
			employeeRole: "",
			editingEmployeeId: null,
		}));
	};

	//************************************************************************************************
	// Custom field management functions
	//************************************************************************************************
	const updateCustomFieldData = useCallback((updates) => {
		setCustomFieldData((prev) => ({ ...prev, ...updates }));
	}, []);

	//************************************************************************************************
	// Custom field choice management functions
	//************************************************************************************************
	/**
	 * Add a new choice to the custom field choices list
	 * @param {string} choice - The choice text to add
	 */
	const addCustomFieldChoice = (choice) => {
		if (!choice.trim()) return;

		setCustomFieldData((prev) => ({
			...prev,
			customFieldOptions: [...prev.customFieldOptions, choice.trim()],
		}));
	};

	/**
	 * Update an existing custom field choice at a specific index
	 * @param {number} index - The index of the choice to update
	 * @param {string} newChoice - The new choice text
	 */
	const updateCustomFieldChoice = (index, newChoice) => {
		setCustomFieldData((prev) => ({
			...prev,
			customFieldOptions: prev.customFieldOptions.map((choice, i) => (i === index ? newChoice : choice)),
		}));
	};

	/**
	 * Remove a custom field choice at a specific index
	 * @param {number} index - The index of the choice to remove
	 */
	const removeCustomFieldChoice = (index) => {
		setCustomFieldData((prev) => ({
			...prev,
			customFieldOptions: prev.customFieldOptions.filter((_, i) => i !== index),
		}));
	};

	/**
	 * Add a new custom field or update an existing one if in edit mode
	 * @param {Object} customField - The custom field object to add or update
	 * @param {string} customField.name - Custom field name
	 * @param {string} customField.type - Custom field type
	 * @param {string[]} [customField.options] - Custom field options for select types
	 */
	const addCustomField = (customField) => {
		setCustomFieldData((prev) => {
			// Use options from the passed customField object if available, otherwise use current state
			const optionsToUse = customField.options !== undefined ? customField.options : prev.customFieldOptions;

			if (prev.editingCustomFieldId) {
				return {
					...prev,
					customFields: prev.customFields.map((field) =>
						field.id === prev.editingCustomFieldId
							? {
									...field,
									name: customField.name,
									type: customField.type,
									options: optionsToUse,
								}
							: field
					),
					customFieldName: "",
					customFieldType: "",
					customFieldOptions: [],
					editingCustomFieldId: null,
				};
			} else {
				const newCustomField = {
					...customField,
					options: optionsToUse,
				};
				return {
					...prev,
					customFields: [...prev.customFields, newCustomField],
					customFieldName: "",
					customFieldType: "",
					customFieldOptions: [],
					editingCustomFieldId: null,
				};
			}
		});
	};

	/**
	 * Edit an existing custom field by populating form fields with field data
	 * @param {number} customFieldId - The ID of the custom field to edit
	 */
	const editCustomField = (customFieldId) => {
		const customField = customFieldData.customFields.find((field) => field.id === customFieldId);
		if (customField) {
			setCustomFieldData((prev) => ({
				...prev,
				customFieldName: customField.name,
				customFieldType: { value: customField.type, label: customField.type },
				customFieldOptions: customField.options || [],
				editingCustomFieldId: customFieldId,
			}));
		}
	};

	/**
	 * Delete a custom field from the custom fields list
	 * @param {number} customFieldId - The ID of the custom field to delete
	 */
	const deleteCustomField = (customFieldId) => {
		setCustomFieldData((prev) => ({
			...prev,
			customFields: prev.customFields.filter((field) => field.id !== customFieldId),
		}));
	};

	/**
	 * Cancel the current custom field edit operation and clear form fields
	 */
	const cancelEditCustomField = () => {
		setCustomFieldData((prev) => ({
			...prev,
			customFieldName: "",
			customFieldType: "",
			customFieldOptions: [],
			editingCustomFieldId: null,
		}));
	};

	//************************************************************************************************
	//Employee Group management functions
	//************************************************************************************************
	/**
	 * Add a new employee group or update an existing one if in edit mode
	 */
	const addEmployeeGroup = () => {
		if (!employeeGroupData.employeeGroupName.trim()) {
			return;
		}

		setEmployeeGroupData((prev) => {
			if (prev.editingEmployeeGroupId) {
				return {
					...prev,
					employeeGroups: prev.employeeGroups.map((group) =>
						group.id === prev.editingEmployeeGroupId
							? {
									...group,
									name: prev.employeeGroupName,
									employees: prev.employeeGroupEmployees || [],
									admins: prev.employeeGroupAdmins || [],
									adminPermissions:
										prev.employeeGroupAdminPermissions?.value || prev.employeeGroupAdminPermissions,
								}
							: group
					),
					employeeGroupName: "",
					employeeGroupEmployees: [],
					employeeGroupAdmins: [],
					employeeGroupAdminPermissions: null,
					editingEmployeeGroupId: null,
				};
			} else {
				const newEmployeeGroup = {
					id: Date.now(),
					name: prev.employeeGroupName,
					employees: prev.employeeGroupEmployees || [],
					admins: prev.employeeGroupAdmins || [],
					adminPermissions: prev.employeeGroupAdminPermissions?.value || prev.employeeGroupAdminPermissions,
				};

				return {
					...prev,
					employeeGroups: [...prev.employeeGroups, newEmployeeGroup],
					employeeGroupName: "",
					employeeGroupEmployees: [],
					employeeGroupAdmins: [],
					employeeGroupAdminPermissions: null,
					editingEmployeeGroupId: null,
				};
			}
		});
	};

	const updateEmployeeGroupData = useCallback((updates) => {
		setEmployeeGroupData((prev) => ({ ...prev, ...updates }));
	}, []);

	/**
	 * Edit an existing employee group by populating form fields with group data
	 * @param {number} employeeGroupId - The ID of the employee group to edit
	 */
	const editEmployeeGroup = (employeeGroupId) => {
		const employeeGroup = employeeGroupData.employeeGroups.find((group) => group.id === employeeGroupId);
		if (employeeGroup) {
			setEmployeeGroupData((prev) => ({
				...prev,
				employeeGroupName: employeeGroup.name,
				employeeGroupEmployees: employeeGroup.employees || [],
				employeeGroupAdmins: employeeGroup.admins || [],
				employeeGroupAdminPermissions:
					employeeGroup.adminPermissions === true || employeeGroup.adminPermissions === "true"
						? { label: "True", value: "true" }
						: { label: "False", value: "false" },
				editingEmployeeGroupId: employeeGroupId,
			}));
		}
	};

	/**
	 * Delete an employee group from the employee groups list
	 * @param {number} employeeGroupId - The ID of the employee group to delete
	 */
	const deleteEmployeeGroup = (employeeGroupId) => {
		setEmployeeGroupData((prev) => ({
			...prev,
			employeeGroups: prev.employeeGroups.filter((group) => group.id !== employeeGroupId),
		}));
	};

	/**
	 * Cancel the current employee group edit operation and clear form fields
	 */
	const cancelEditEmployeeGroup = () => {
		setEmployeeGroupData((prev) => ({
			...prev,
			employeeGroupName: "",
			employeeGroupEmployees: [],
			employeeGroupAdmins: [],
			employeeGroupAdminPermissions: null,
			editingEmployeeGroupId: null,
		}));
	};

	//************************************************************************************************
	//custom review form functions
	//************************************************************************************************

	const updateCustomReviewFormData = useCallback((updates) => {
		setCustomReviewFormData((prev) => ({ ...prev, ...updates }));
	}, []);

	//************************************************************************************************
	// Review template management functions
	//************************************************************************************************
	const updateReviewTemplateData = useCallback((updates) => {
		setReviewTemplateData((prev) => ({ ...prev, ...updates }));
	}, []);

	//************************************************************************************************
	// Review template form management functions
	//************************************************************************************************
	/**
	 * Add a new form to a review template
	 * @param {string} formType - The type of form to add ("regular", "360", or "external")
	 */
	const addReviewTemplateForm = (formType) => {
		const newFormId = Date.now();
		const newForm = {
			id: newFormId,
			form: "",
		};

		if (formType === "regular") {
			newForm.author = null;
			newForm.signer = null;
			newForm.visibility = null;
			newForm.redact = null;
			newForm.authorDaysToComplete = "";
			newForm.signerDaysToComplete = "";
		} else if (formType === "360") {
			newForm.whoCanAddForm = null;
			newForm.signer = null;
			newForm.authorDaysToComplete = "";
			newForm.signerDaysToComplete = "";
			newForm.approvalRequired = null;
			newForm.visibility = null;
			newForm.allowRequestMessage = null;
			newForm.redact = null;
		} else if (formType === "external") {
			newForm.reviewerName = "";
			newForm.reviewerEmail = "";
			newForm.message = "";
			newForm.visibility = null;
			newForm.redact = null;
		}

		setReviewTemplateData((prev) => ({
			...prev,
			[`forms_${formType}`]: [...prev[`forms_${formType}`], newForm],
		}));
	};

	/**
	 * Update a specific form in a review template
	 * @param {string} formType - The type of form ("regular", "360", or "external")
	 * @param {number} formId - The ID of the form to update
	 * @param {Object} updates - The updates to apply to the form
	 */
	const updateReviewTemplateForm = (formType, formId, updates) => {
		setReviewTemplateData((prev) => ({
			...prev,
			[`forms_${formType}`]: prev[`forms_${formType}`].map((form) =>
				form.id === formId ? { ...form, ...updates } : form
			),
		}));
	};

	/**
	 * Remove a form from a review template
	 * @param {string} formType - The type of form ("regular", "360", or "external")
	 * @param {number} formId - The ID of the form to remove
	 */
	const removeReviewTemplateForm = (formType, formId) => {
		setReviewTemplateData((prev) => ({
			...prev,
			[`forms_${formType}`]: prev[`forms_${formType}`].filter((form) => form.id !== formId),
		}));
	};

	/**
	 * Add a new review template or update an existing one if in edit mode
	 * @param {Object} template - The review template object to add or update
	 * @param {string} template.name - Template name
	 * @param {Array} template.forms - Forms array
	 * @param {Array} template.forms_360 - 360 forms array
	 * @param {Array} template.external_forms - External forms array
	 */
	const addReviewTemplate = (template) => {
		setReviewTemplateData((prev) => {
			if (prev.editingReviewTemplateId) {
				return {
					...prev,
					reviewTemplates: prev.reviewTemplates.map((t) =>
						t.id === prev.editingReviewTemplateId
							? {
									...t,
									name: template.name,
									forms: template.forms,
									forms_360: template.forms_360,
									external_forms: template.external_forms,
								}
							: t
					),
					reviewTemplateName: "",
					selectedFormType: null,
					selectedForm: null,
					tempFormData: {},
					editingReviewTemplateId: null,
					forms: [],
					forms_360: [],
					external_forms: [],
				};
			}
			return {
				...prev,
				reviewTemplates: [...prev.reviewTemplates, template],
				reviewTemplateName: "",
				selectedFormType: null,
				selectedForm: null,
				tempFormData: {},
				editingReviewTemplateId: null,
				forms: [],
				forms_360: [],
				external_forms: [],
			};
		});
	};

	const editReviewTemplate = (templateId) => {
		const template = reviewTemplateData.reviewTemplates.find((t) => t.id === templateId);
		if (template) {
			setReviewTemplateData((prev) => ({
				...prev,
				reviewTemplateName: template.name,
				forms: template.forms || [],
				forms_360: template.forms_360 || [],
				external_forms: template.external_forms || [],
				editingReviewTemplateId: templateId,
			}));
		}
	};

	const deleteReviewTemplate = (templateId) => {
		setReviewTemplateData((prev) => ({
			...prev,
			reviewTemplates: prev.reviewTemplates.filter((t) => t.id !== templateId),
		}));
	};

	//************************************************************************************************
	// Question management functions
	//************************************************************************************************
	const addQuestion = () => {
		setCustomReviewFormData((prev) => ({
			...prev,
			customReviewQuestions: [
				...prev.customReviewQuestions,
				{ id: Date.now(), type: "", question: "", choices: [] },
			],
		}));
	};

	const updateQuestion = (questionId, updates) => {
		setCustomReviewFormData((prev) => ({
			...prev,
			customReviewQuestions: prev.customReviewQuestions.map((q) =>
				q.id === questionId ? { ...q, ...updates } : q
			),
		}));
	};

	const removeQuestion = (questionId) => {
		setCustomReviewFormData((prev) => ({
			...prev,
			customReviewQuestions: prev.customReviewQuestions.filter((q) => q.id !== questionId),
		}));
	};

	const addCustomReviewForm = (form) => {
		setCustomReviewFormData((prev) => {
			if (prev.editingCustomReviewFormId) {
				return {
					...prev,
					customReviewForms: prev.customReviewForms.map((f) =>
						f.id === prev.editingCustomReviewFormId
							? { ...f, name: form.name, questions: form.questions }
							: f
					),
					customReviewFormName: "",
					customReviewQuestions: [{ id: Date.now(), type: "", question: "", choices: [] }],
					editingCustomReviewFormId: null,
				};
			} else {
				return {
					...prev,
					customReviewForms: [...prev.customReviewForms, form],
					customReviewFormName: "",
					customReviewQuestions: [{ id: Date.now(), type: "", question: "", choices: [] }],
					editingCustomReviewFormId: null,
				};
			}
		});
	};

	const editCustomReviewForm = (formId) => {
		const form = customReviewFormData.customReviewForms.find((form) => form.id === formId);
		if (form) {
			setCustomReviewFormData((prev) => ({
				...prev,
				customReviewFormName: form.name,
				customReviewQuestions: form.questions,
				editingCustomReviewFormId: formId,
			}));
		}
	};
	const deleteCustomReviewForm = (formId) => {
		setCustomReviewFormData((prev) => ({
			...prev,
			customReviewForms: prev.customReviewForms.filter((form) => form.id !== formId),
		}));
	};

	const cancelEditCustomReviewForm = () => {
		setCustomReviewFormData((prev) => ({
			...prev,
			customReviewFormName: "",
			customReviewQuestions: [{ id: Date.now(), type: "", question: "", choices: [] }],
			editingCustomReviewFormId: null,
		}));
	};

	//************************************************************************************************
	// Review cycle management functions
	//************************************************************************************************
	const updateReviewCycleData = useCallback((updates) => {
		setReviewCycleData((prev) => ({ ...prev, ...updates }));
	}, []);

	const addReviewCycle = () => {
		setReviewCycleData((prev) => {
			if (prev.editingReviewCycleId) {
				return {
					...prev,
					reviewCycles: prev.reviewCycles.map((cycle) =>
						cycle.id === prev.editingReviewCycleId
							? {
									...cycle,
									name: prev.reviewCycleName,
									template: prev.reviewTemplate?.label || "",
									subjects: prev.subjects.map((s) => s.label || s.name || s),
									start_date: prev.startDate.toISOString(),
									admins_only: prev.admin_only?.value === "true",
									reviews: [],
									easy_add_configs: [],
									department: null,
								}
							: cycle
					),
					reviewCycleName: "",
					reviewTemplate: "",
					subjects: [],
					startDate: new Date(),
					admin_only: false,
					editingReviewCycleId: null,
				};
			} else {
				const newReviewCycle = {
					id: Date.now(),
					name: prev.reviewCycleName,
					template: prev.reviewTemplate?.label || "",
					subjects: prev.subjects.map((s) => s.label || s.name || s),
					start_date: prev.startDate.toISOString(),
					admins_only: prev.admin_only?.value === "true",
					reviews: [],
					easy_add_configs: [],
					department: null,
				};

				return {
					...prev,
					reviewCycles: [...prev.reviewCycles, newReviewCycle],
					reviewCycleName: "",
					reviewTemplate: "",
					subjects: [],
					startDate: new Date(),
					admin_only: false,
					editingReviewCycleId: null,
				};
			}
		});
	};

	const editReviewCycle = (cycleId) => {
		const cycle = reviewCycleData.reviewCycles.find((c) => c.id === cycleId);
		if (cycle) {
			setReviewCycleData((prev) => ({
				...prev,
				reviewCycleName: cycle.name,
				reviewTemplate: { label: cycle.template, value: cycle.template },
				subjects: cycle.subjects?.map((s) => ({ label: s, value: s })) || [],
				startDate: typeof cycle.start_date === "function" ? cycle.start_date() : new Date(cycle.start_date),
				admin_only: {
					label: cycle.admins_only ? "True" : "False",
					value: cycle.admins_only ? "true" : "false",
				},
				editingReviewCycleId: cycleId,
			}));
		}
	};

	const deleteReviewCycle = (cycleId) => {
		setReviewCycleData((prev) => ({
			...prev,
			reviewCycles: prev.reviewCycles.filter((c) => c.id !== cycleId),
		}));
	};

	//************************************************************************************************
	// Goals management functions
	//************************************************************************************************
	const updateGoalsData = useCallback((updates) => {
		setGoalsData((prev) => ({ ...prev, ...updates }));
	}, []);

	const addGoal = () => {
		setGoalsData((prev) => {
			if (prev.editingGoalId) {
				return {
					...prev,
					goals: prev.goals.map((goal) =>
						goal.id === prev.editingGoalId
							? {
									...goal,
									name: prev.goalName,
									description: prev.goalDescription,
									assignees: (prev.goalAssignee || []).map((a) => a.id || a),
									owner: prev.goalOwner?.id || prev.goalOwner?.value || prev.goalOwner || "",
									target: parseInt(prev.goalTarget) || 0,
									units: prev.goalUnits,
									value: parseInt(prev.goalValue) || 0,
									startDate: prev.startDate,
								}
							: goal
					),
					goalName: "",
					goalDescription: "",
					goalAssignee: [],
					goalOwner: "",
					goalTarget: "",
					goalUnits: "",
					goalValue: "",
					startDate: new Date(),
					editingGoalId: null,
				};
			} else {
				const newGoal = {
					id: Date.now(),
					name: prev.goalName,
					description: prev.goalDescription,
					assignees: (prev.goalAssignee || []).map((a) => a.id || a),
					owner: prev.goalOwner?.id || prev.goalOwner?.value || prev.goalOwner || "",
					target: parseInt(prev.goalTarget) || 0,
					units: prev.goalUnits,
					value: parseInt(prev.goalValue) || 0,
					startDate: prev.startDate,
				};

				return {
					...prev,
					goals: [...prev.goals, newGoal],
					goalName: "",
					goalDescription: "",
					goalAssignee: [],
					goalOwner: "",
					goalTarget: "",
					goalUnits: "",
					goalValue: "",
					startDate: new Date(),
					editingGoalId: null,
				};
			}
		});
	};

	const editGoal = (goalId) => {
		const goal = goalsData.goals.find((g) => g.id === goalId);
		if (goal) {
			let ownerForForm = "";
			if (goal.owner) {
				const ownerEmployee = employeeData.employees.find((emp) => emp.id === goal.owner);
				if (ownerEmployee) {
					ownerForForm = { label: ownerEmployee.name, id: ownerEmployee.id };
				}
			}

			setGoalsData((prev) => ({
				...prev,
				goalName: goal.name,
				goalDescription: goal.description,
				goalAssignee: goal.assignees || [],
				goalOwner: ownerForForm,
				goalTarget: goal.target.toString(),
				goalUnits: goal.units,
				goalValue: goal.value.toString(),
				startDate: goal.startDate,
				editingGoalId: goalId,
			}));
		}
	};

	const deleteGoal = (goalId) => {
		setGoalsData((prev) => ({
			...prev,
			goals: prev.goals.filter((g) => g.id !== goalId),
		}));
	};

	const cancelEditGoal = () => {
		setGoalsData((prev) => ({
			...prev,
			goalName: "",
			goalDescription: "",
			goalAssignee: [],
			goalOwner: "",
			goalTarget: "",
			goalUnits: "",
			goalValue: "",
			startDate: new Date(),
			editingGoalId: null,
		}));
	};

	//************************************************************************************************
	// Reset functions
	//************************************************************************************************

	const resetEmployeeData = useCallback(() => {
		setEmployeeData({
			employees: [],
			employeeName: "",
			employeeManager: "",
			employeeRole: "",
			editingEmployeeId: null,
		});
	}, []);

	const resetCustomFieldData = useCallback(() => {
		setCustomFieldData({
			customFields: [],
			customFieldName: "",
			customFieldType: "",
			customFieldOptions: [],
			editingCustomFieldId: null,
		});
	}, []);

	const resetEmployeeGroupData = useCallback(() => {
		setEmployeeGroupData({
			employeeGroups: [],
			employeeGroupName: "",
			employeeGroupEmployees: [],
			employeeGroupAdmins: [],
			employeeGroupAdminPermissions: null,
			editingEmployeeGroupId: null,
		});
	}, []);

	const resetCustomReviewFormData = useCallback(() => {
		setCustomReviewFormData({
			customReviewForms: [],
			customReviewFormName: "",
			customReviewQuestions: [{ id: Date.now(), type: "", question: "", choices: [] }],
			editingCustomReviewFormId: null,
		});
	}, []);

	const resetReviewTemplateData = useCallback(() => {
		setReviewTemplateData({
			reviewTemplateName: "",
			reviewTemplates: [],
			selectedFormType: null,
			selectedForm: null,
			tempFormData: {},
			editingReviewTemplateId: null,
			forms: [],
			forms_360: [],
			external_forms: [],
		});
	}, []);

	const resetReviewCycleData = useCallback(() => {
		setReviewCycleData({
			reviewCycles: [],
			reviewCycleName: "",
			reviewTemplate: "",
			subjects: [],
			startDate: new Date(),
			admin_only: false,
			reviews: [],
			easy_add_configs: [],
			department: null,
			editingReviewCycleId: null,
		});
	}, []);

	const resetGoalsData = useCallback(() => {
		setGoalsData({
			goals: [],
			goalId: "",
			goalName: "",
			goalDescription: "",
			goalAssignee: [],
			goalOwner: "",
			goalTarget: "",
			goalUnits: "",
			goalValue: "",
			startDate: new Date(),
			editingGoalId: null,
		});
	}, []);

	const resetAllCustomizedData = useCallback(() => {
		resetEmployeeData();
		resetCustomFieldData();
		resetEmployeeGroupData();
		resetCustomReviewFormData();
		resetReviewTemplateData();
		resetReviewCycleData();
		resetGoalsData();
	}, [
		resetEmployeeData,
		resetCustomFieldData,
		resetEmployeeGroupData,
		resetCustomReviewFormData,
		resetReviewTemplateData,
		resetReviewCycleData,
		resetGoalsData,
	]);

	const cancelEditReviewTemplate = () => {
		setReviewTemplateData((prev) => ({
			...prev,
			reviewTemplateName: "",
			selectedFormType: null,
			selectedForm: null,
			tempFormData: {},
			editingReviewTemplateId: null,
			forms: [],
			forms_360: [],
			external_forms: [],
		}));
	};

	const cancelEditReviewCycle = () => {
		setReviewCycleData((prev) => ({
			...prev,
			reviewCycleName: "",
			reviewTemplate: "",
			subjects: [],
			startDate: new Date(),
			admin_only: false,
			editingReviewCycleId: null,
		}));
	};

	const value = {
		// Employee data
		employeeData,
		setEmployeeData,
		updateEmployeeData,
		addEmployee,
		editEmployee,
		deleteEmployee,
		cancelEditEmployee,
		resetEmployeeData,

		// Custom field data
		customFieldData,
		setCustomFieldData,
		updateCustomFieldData,
		addCustomField,
		editCustomField,
		deleteCustomField,
		cancelEditCustomField,
		resetCustomFieldData,
		addCustomFieldChoice,
		updateCustomFieldChoice,
		removeCustomFieldChoice,

		// Employee group data
		employeeGroupData,
		setEmployeeGroupData,
		updateEmployeeGroupData,
		addEmployeeGroup,
		editEmployeeGroup,
		deleteEmployeeGroup,
		cancelEditEmployeeGroup,
		resetEmployeeGroupData,

		// Review form data
		customReviewFormData,
		setCustomReviewFormData,
		updateCustomReviewFormData,
		addQuestion,
		updateQuestion,
		removeQuestion,
		addCustomReviewForm,
		editCustomReviewForm,
		deleteCustomReviewForm,
		cancelEditCustomReviewForm,
		resetCustomReviewFormData,

		// Review template data
		reviewTemplateData,
		updateReviewTemplateData,
		addReviewTemplateForm,
		updateReviewTemplateForm,
		removeReviewTemplateForm,
		addReviewTemplate,
		editReviewTemplate,
		deleteReviewTemplate,
		resetReviewTemplateData,
		cancelEditReviewTemplate,

		// Review cycle data
		reviewCycleData,
		updateReviewCycleData,
		addReviewCycle,
		editReviewCycle,
		deleteReviewCycle,
		resetReviewCycleData,
		cancelEditReviewCycle,

		// Goals data
		goalsData,
		updateGoalsData,
		addGoal,
		editGoal,
		deleteGoal,
		cancelEditGoal,
		resetGoalsData,
		// Reset all
		resetAllCustomizedData,
	};

	return <CustomizedDataContext.Provider value={value}>{children}</CustomizedDataContext.Provider>;
};
